import * as THREE from 'three';
import type { DrawingEntity, Vec3 } from '../types';
import { faceBasis } from './faces';

export type FaceSurface = { kind: 'top' } | { kind: 'base' } | { kind: 'side'; edgeIndex: number };

const direction = (value: THREE.Vector3): Vec3 => [value.x, value.y, value.z].map((coordinate) => Math.abs(coordinate) < 1e-10 ? 0 : coordinate) as Vec3;

export function faceSurfaceNormal(drawing: DrawingEntity, surface: FaceSurface): Vec3 | null {
  const vertices = drawing.vertices3d;
  const basis = vertices && faceBasis(vertices);
  if (!vertices || !basis) return null;
  const depthSign = Math.sign(drawing.extrusionMm || 1);
  if (surface.kind === 'top') return direction(basis.normal.clone().multiplyScalar(depthSign));
  if (surface.kind === 'base') return direction(basis.normal.clone().multiplyScalar(-depthSign));
  const index = surface.edgeIndex;
  if (index < 0 || index >= vertices.length) return null;
  const edge = new THREE.Vector3(...vertices[(index + 1) % vertices.length]).sub(new THREE.Vector3(...vertices[index]));
  if (edge.lengthSq() < .001) return null;
  return direction(edge.normalize().cross(basis.normal).multiplyScalar(Math.sign(basis.area)).normalize());
}

/** Match a raycast triangle to the actual cap or side of an extruded drawing. */
export function pickFaceSurface(drawing: DrawingEntity, normal: Vec3): FaceSurface | null {
  const vertices = drawing.vertices3d;
  if (!vertices || !faceBasis(vertices)) return null;
  const hit = new THREE.Vector3(...normal).normalize();
  const candidates: FaceSurface[] = [{ kind: 'top' }, { kind: 'base' }, ...vertices.map((_, edgeIndex) => ({ kind: 'side' as const, edgeIndex }))];
  let best: FaceSurface | null = null, score = .7;
  for (const candidate of candidates) {
    const candidateNormal = faceSurfaceNormal(drawing, candidate);
    if (!candidateNormal) continue;
    const next = hit.dot(new THREE.Vector3(...candidateNormal));
    if (next > score) { best = candidate; score = next; }
  }
  return best;
}

const crosses = (a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }) =>
  (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);

function validBoundary(vertices: Vec3[], originalArea: number) {
  const basis = faceBasis(vertices);
  if (!basis || Math.sign(basis.area) !== Math.sign(originalArea)) return false;
  for (let i = 0; i < vertices.length; i += 1) {
    const a = new THREE.Vector3(...vertices[i]), b = new THREE.Vector3(...vertices[(i + 1) % vertices.length]);
    if (a.distanceTo(b) < 1) return false;
  }
  const points = basis.points;
  for (let i = 0; i < points.length; i += 1) for (let j = i + 2; j < points.length; j += 1) {
    if (i === 0 && j === points.length - 1) continue;
    const a = points[i], b = points[(i + 1) % points.length], c = points[j], d = points[(j + 1) % points.length];
    if (crosses(a, b, c) * crosses(a, b, d) < 0 && crosses(c, d, a) * crosses(c, d, b) < 0) return false;
  }
  return true;
}

/** Return a new solid after moving exactly the clicked face by a signed distance. */
export function pushPullFaceSurface(drawing: DrawingEntity, surface: FaceSurface, distanceMm: number): DrawingEntity | null {
  const vertices = drawing.vertices3d;
  const basis = vertices && faceBasis(vertices);
  const normal = faceSurfaceNormal(drawing, surface);
  if (drawing.kind !== 'face' || !vertices || !basis || !normal || !Number.isFinite(distanceMm) || distanceMm === 0) return null;
  const nextVertices: Vec3[] = vertices.map((point) => [...point] as Vec3);
  let extrusionMm = drawing.extrusionMm ?? 0;
  if (surface.kind === 'top') extrusionMm += distanceMm * Math.sign(extrusionMm || 1);
  else if (surface.kind === 'base') {
    for (const point of nextVertices) for (let axis = 0; axis < 3; axis += 1) point[axis] += normal[axis] * distanceMm;
    extrusionMm += distanceMm * Math.sign(extrusionMm || 1);
  } else {
    for (const index of [surface.edgeIndex, (surface.edgeIndex + 1) % vertices.length]) {
      for (let axis = 0; axis < 3; axis += 1) nextVertices[index][axis] += normal[axis] * distanceMm;
    }
    if (!validBoundary(nextVertices, basis.area)) return null;
  }
  if (!Number.isFinite(extrusionMm)) return null;
  return { ...drawing, vertices3d: nextVertices, points: nextVertices.map(([x, , z]) => [x, z]), extrusionMm };
}
